import { PlusOutlined } from '@ant-design/icons'
import type { Ratebook } from '@api/useFreight/types'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { GraviButton, GraviGrid, Horizontal } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import type { ColDef, GridApi, IFilter } from 'ag-grid-community'

import { RatebookCreationPopover } from './RatebookCreationPopover'
import { useRatebooksState } from './hooks/useRatebooksState'
import type { RatebookType } from './types'
import type { GridConfigState } from '@gravitate-js/excalibrr/dist/components/GraviGrid/hooks/useAGGridEvent'
import type { MutableRefObject } from 'react'
import { gridViewManager } from '@/utils/grid'

export function RatebooksPage({ type }: { type: RatebookType }) {
  const queryClient = useQueryClient()
  const {
    gridAPIRef,
    ratebooks,
    isLoading,
    updateSetting,
    canEdit,
    columnDefs,
    agPropOverrides,
    exportPointToPointRates,
    importPointToPointRates,
    exportBaseFreight,
    importBaseFreight,
    exportSurcharges,
    importSurcharges,
  } = useRatebooksState(type)

  return (
    <GraviGrid<Ratebook, IFilter>
      rowData={ratebooks}
      columnDefs={columnDefs}
      primaryKey='_id'
      loading={isLoading}
      controlBarProps={{
        title: `${type === 'customer' ? 'Customer' : 'Carrier'} Ratebooks`,
        actionButtons: (
          <Horizontal verticalCenter style={{ gap: '1em' }}>
            <UploadDownloadButtons
              refreshCallback={() => queryClient.invalidateQueries({ queryKey: ['ratebooks', type] })}
              downloadEP={() => exportBaseFreight()}
              uploadEP={canEdit && ((file: FormData) => importBaseFreight(file))}
              fileName='FreightRates.xlsx'
              downloadEPData={null}
              normalizeFunction={null}
              asOfDate={null}
              excelOptions={[
                {
                  title: 'Download Freight Table Rates',
                  downloadEP: () =>
                    exportBaseFreight({
                      book_type: type === 'customer' ? 'Revenue' : 'Cost',
                    } as Ratebook),
                  fileName: 'FreightTableRates.xlsx',
                },
                {
                  title: 'Upload Freight Table Rates',
                  uploadEP: (formData: FormData) =>
                    importBaseFreight(formData, {
                      book_type: type === 'customer' ? 'Revenue' : 'Cost',
                    } as Ratebook),
                },
                {
                  title: 'Download Point to Point Rates',
                  downloadEP: () =>
                    exportPointToPointRates({
                      book_type: type === 'customer' ? 'Revenue' : 'Cost',
                    } as Ratebook),
                  fileName: 'PointToPointRates.xlsx',
                },
                {
                  title: 'Upload Point to Point Rates',
                  uploadEP: (formData: FormData) =>
                    importPointToPointRates(formData, {
                      book_type: type === 'customer' ? 'Revenue' : 'Cost',
                    } as Ratebook),
                },
                {
                  title: 'Download Surcharge Rates',
                  downloadEP: () =>
                    exportSurcharges({
                      book_type: type === 'customer' ? 'Revenue' : 'Cost',
                    } as Ratebook),
                  fileName: 'SurchargeRates.xlsx',
                },
                {
                  title: 'Upload Surcharge Rates',
                  uploadEP: (formData: FormData) =>
                    importSurcharges(formData, {
                      book_type: type === 'customer' ? 'Revenue' : 'Cost',
                    } as Ratebook),
                },
              ]}
            />
            <RatebookCreationPopover type={type}>
              <GraviButton theme2 icon={<PlusOutlined />}>
                New Ratebook
              </GraviButton>
            </RatebookCreationPopover>
          </Horizontal>
        ),
      }}
      externalRef={gridAPIRef as MutableRefObject<GridApi<Ratebook>>}
      storageKey={`${type}-ratebook`}
      onGridConfigChanged={(config: GridConfigState) => updateSetting(`gridConfig::${type}-ratebooks`, config)}
      agPropOverrides={{
        ...agPropOverrides,
        columnDefs: columnDefs as ColDef<Ratebook>[],
      }}
      {...gridViewManager(`${type}-ratebook`, updateSetting)}
    />
  )
}
